import { useMemo, useState } from 'react';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/cms/ui/dialog';
import { IconButton } from '@/components/ui/icon-button';
import { FieldLabel } from '@/components/ui/text';
import { ImageButton } from '@/components/ui/image-button';
import { TemplateScene } from '@/types/cmsTypes';
import { ArrowLeftIcon, CheckIcon, PlayIcon } from '@phosphor-icons/react';
import {
  CATEGORY_PILL,
  DIFFICULTY_ICON,
  DIFFICULTY_PILL,
  PILL_BASE,
  TAG_PILL,
} from '@/components/cms/ui/pill-styles';
import projectPlaceholder from '@/assets/project-historical.jpg';
import { getSquiggleCropStyle } from '@/utils/squiggleCrop';
import { templateFeatureLabel } from '@/components/publish-flow/templateOptions';
import LureoButton from '@/common/LureoButton';
import { TemplateExistingVersionDialog } from './template-existing-version-dialog';
import { useOpenTemplateScene } from './useOpenTemplateScene';
import { isMobileDevice } from '@/utils/deviceDetection';
import { desktopOnlyCopy } from '@/components/handoff/desktopOnlyCopy';

interface TemplateSceneModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: TemplateScene | null;
}

type MediaItem = {
  type: 'image' | 'video';
  url: string;
  isPlaceholder?: boolean;
};

function buildMediaItems(template: TemplateScene): MediaItem[] {
  const items: MediaItem[] = [];
  if (template.videoUrl) {
    items.push({ type: 'video', url: template.videoUrl });
  }
  items.push({
    type: 'image',
    url: template.heroImage,
    isPlaceholder: template.heroImage === projectPlaceholder,
  });
  for (const imageUrl of template.templateLandscapeImages ?? []) {
    items.push({
      type: 'image',
      url: imageUrl,
      isPlaceholder: imageUrl === projectPlaceholder,
    });
  }
  return items;
}

export function TemplateSceneModal({ open, onOpenChange, template: incomingTemplate }: TemplateSceneModalProps) {
  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
  // Keep the last template rendered while closing so the exit animation can play.
  const [template, setTemplate] = useState(incomingTemplate);
  if (incomingTemplate && incomingTemplate !== template) setTemplate(incomingTemplate);

  const action = useOpenTemplateScene({ template, open, onClose: () => onOpenChange(false) });

  const carouselItems = useMemo(
    () => (template ? buildMediaItems(template) : []),
    [template],
  );

  // Each new template opens on its hero image.
  const [mediaTemplate, setMediaTemplate] = useState<TemplateScene | null>(null);
  if (template && template !== mediaTemplate) {
    setMediaTemplate(template);
    const hero =
      carouselItems.find((i) => i.type === 'image' && i.url === template.heroImage) ??
      carouselItems[0];
    setSelectedMedia(hero ?? null);
  }

  if (!template) return null;

  const DifficultyIcon = template.difficulty ? DIFFICULTY_ICON[template.difficulty] : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Its back button closes it, so it has no close button of its own; wider than xl on a wide screen, as production's */}
      <DialogContent width="xl" hideCloseButton className="sm:max-w-2xl md:max-w-4xl lg:max-w-6xl">
        <DialogHeader>
          <div className="flex min-w-0 items-center gap-3">
            <IconButton label="Go back" place="dialog" onClick={() => onOpenChange(false)} className="h-9 w-9">
              <ArrowLeftIcon className="h-4 w-4 sm:h-5 sm:w-5" />
            </IconButton>
            <DialogTitle className="truncate sm:text-lureo-20">{template.title}</DialogTitle>
          </div>
          <DialogDescription className="sr-only">
            Template scene details, media, and options to open or copy.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b-lureo-border-width border-lureo-line bg-lureo-neutral-strong/25 px-6 py-2.5">
          {template.category && (
            <span className={`${PILL_BASE} capitalize ${CATEGORY_PILL}`}>{template.category}</span>
          )}
          {template.difficulty && DifficultyIcon && (
            <span className={`${PILL_BASE} ${DIFFICULTY_PILL[template.difficulty]}`}>
              <DifficultyIcon className="h-3.5 w-3.5" />
              {template.difficulty.charAt(0).toUpperCase() + template.difficulty.slice(1)}
            </span>
          )}
          {template.duration > 0 && (
            <>
              <span className="hidden h-4 w-px bg-lureo-line-strong sm:block" />
              <span className="font-lureo-font-ui text-lureo-12 font-semibold tabular-nums text-lureo-ink leading-4">
                {template.duration} min
              </span>
            </>
          )}
          {template.locationInfo?.address && (
            <>
              <span className="hidden h-4 w-px bg-lureo-line-strong sm:block" />
              <span className="w-full truncate font-lureo-font-ui text-lureo-12 text-lureo-ink-subtle sm:w-auto sm:whitespace-normal leading-4">{template.locationInfo.address}</span>
            </>
          )}
          {template.tags.length > 0 && (
            <>
              <span className="hidden h-4 w-px bg-lureo-line-strong sm:block" />
              {template.tags.map((tag) => (
                <span key={tag} className={`${PILL_BASE} ${TAG_PILL}`}>
                  {tag}
                </span>
              ))}
            </>
          )}
        </div>

        <DialogBody className="sm:py-6">
          <div className="grid grid-cols-1 items-start gap-5 sm:gap-6 lg:grid-cols-[2fr_1fr]">
            <div className="min-w-0">
              <div className="relative aspect-video w-full overflow-hidden rounded-lureo-radius bg-lureo-neutral drop-shadow-md">
                {selectedMedia?.type === 'video' ? (
                  <video src={selectedMedia.url} controls className="h-full w-full object-cover" preload="metadata" />
                ) : selectedMedia?.isPlaceholder ? (
                  <div
                    className="h-full w-full"
                    style={getSquiggleCropStyle(template.id, { scaleRange: [3, 4], size: 'quarter' })}
                  />
                ) : selectedMedia ? (
                  <img src={selectedMedia.url} alt={template.title} className="h-full w-full object-cover" />
                ) : null}
              </div>

              {carouselItems.length > 1 && (
                <div className="mt-3 flex gap-2 overflow-x-auto pb-1 sm:flex-wrap sm:gap-3">
                  {carouselItems.map((item, index) => {
                    const isSelected =
                      selectedMedia?.url === item.url && selectedMedia?.type === item.type;
                    return (
                      <ImageButton
                        key={`${item.type}-${index}`}
                        label={`Show ${item.type} ${index + 1}`}
                        selected={isSelected}
                        onClick={() => setSelectedMedia(item)}
                        className="aspect-video w-16 shrink-0 sm:w-28"
                      >
                        {item.type === 'video' ? (
                          <>
                            <video src={item.url} className="h-full w-full object-cover" preload="metadata" muted playsInline />
                            <span className="absolute inset-0 flex items-center justify-center bg-lureo-emphasis/20">
                              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-lureo-surface/90">
                                <PlayIcon className="h-3.5 w-3.5 text-lureo-ink" weight="fill" />
                              </span>
                            </span>
                          </>
                        ) : item.isPlaceholder ? (
                          <div
                            className="h-full w-full"
                            style={getSquiggleCropStyle(`${template.id}-${index}`, { scaleRange: [3, 4], size: 'quarter' })}
                          />
                        ) : (
                          <img
                            src={item.url}
                            alt={`${template.title} ${index + 1}`}
                            className="h-full w-full object-cover"
                          />
                        )}
                      </ImageButton>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="min-w-0 divide-y-lureo-border-width divide-lureo-line">
              <Section label="Description">
                <p className="whitespace-pre-line font-lureo-font-prose text-lureo-14 leading-relaxed text-lureo-ink-subtle">
                  {template.description}
                </p>
              </Section>

              {template.learnings && (
                <Section label="What you'll learn">
                  <p className="whitespace-pre-line font-lureo-font-prose text-lureo-14 leading-relaxed text-lureo-ink-subtle">
                    {template.learnings}
                  </p>
                </Section>
              )}

              {template.features && template.features.length > 0 && (
                <Section label="Features it shows off">
                  <ul className="flex flex-col gap-1.5">
                    {template.features.map((key) => (
                      <li key={key} className="flex items-start gap-2">
                        <span className="mt-0.5 flex h-[15px] w-[15px] shrink-0 items-center justify-center rounded-lureo-radius bg-lureo-brand text-lureo-on-brand">
                          <CheckIcon className="h-2.5 w-2.5" />
                        </span>
                        <span className="font-lureo-font-ui text-lureo-13 font-medium leading-snug text-lureo-ink">
                          {templateFeatureLabel(key)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </Section>
              )}
            </div>
          </div>
        </DialogBody>

        {/* The note on what Open does, then Open */}
        <DialogFooter className="sm:justify-between sm:gap-4">
          <p className="min-w-0 whitespace-nowrap text-center font-lureo-font-ui text-lureo-14 leading-snug text-lureo-ink-subtle sm:whitespace-normal sm:text-left sm:text-lureo-12 sm:leading-4">
            {isMobileDevice()
              ? desktopOnlyCopy.heading
              : action.isOwnTemplate
                ? 'Thanks for submitting this template. Open gives you your own copy to work from.'
                : action.canEditDirectly
                  ? 'Opens the live template for editing.'
                  : 'Opens your own copy in My Templates. The original stays as it is.'}
          </p>
          <LureoButton
            variant="secondary"
            suppressTooltip
            onClick={action.open}
            disabled={action.isLoading || isMobileDevice()}
            className="h-10 min-h-[44px] w-full shrink-0 rounded-lureo-radius-button-pill px-4 text-lureo-14 leading-snug sm:h-12 sm:min-h-0 sm:w-40 sm:px-6 sm:text-lureo-16"
          >
            {action.isLoading ? 'Opening...' : 'Open'}
          </LureoButton>
        </DialogFooter>
      </DialogContent>

      <TemplateExistingVersionDialog
        open={action.existingVersionOpen}
        onOpenChange={action.setExistingVersionOpen}
        onContinueExisting={action.continueExisting}
        onStartFresh={action.startFresh}
        templateName={template.title}
        copyName={action.existingCopyName}
      />
    </Dialog>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className="py-4 first:pt-0 last:pb-0">
      <FieldLabel mb>{label}</FieldLabel>
      {children}
    </section>
  );
}
